/* eslint-disable complexity */
/* eslint-disable camelcase */
import '@fontsource/nunito/variable.css';
import { Script, ScriptStrategy } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet';

import { propTypesCategory } from '../../../templates/Category/Category.proptypes';
import { propTypesContact } from '../../../templates/Contact/Contact.proptypes';
import { propTypesEvent } from '../../../templates/Event/Event.proptypes';
import { propTypesEventTag } from '../../../templates/EventTag/EventTag.proptypes';
import { propTypesHomepage } from '../../../templates/Homepage/Homepage.proptypes';
import { propTypesHomepageWeb } from '../../../templates/HomepageWeb/HomepageWeb.proptypes';
import { propTypesNewsletter } from '../../../templates/Newsletter/Newsletter.proptypes';
import { propTypesOrphans } from '../../../templates/Orphans/Orphans.proptypes';
import { propTypesPage } from '../../../templates/Page/Page.proptypes';
import { propTypesPost } from '../../../templates/Post/Post.proptypes';
import { propTypesPricing } from '../../../templates/Pricing/Pricing.proptypes';
import { propTypesResource } from '../../../templates/Resource/Resource.proptypes';
import { propTypesTag } from '../../../templates/Tag/Tag.proptypes';
import { propTypesTestimonial } from '../../../templates/Testimonial/Testimonial.proptypes';
import { propTypesWoobinar } from '../../../templates/Woobinar/Woobinar.proptypes';
import {
  propTypesEventCategory,
  propTypesFAQContent,
  propTypesReviews,
  propTypesSite,
} from '../../constants/Proptypes';

import favMs144 from '../../../../assets/images/ms-icon-144x144.avif';
import { seoIconsLinks } from '../../constants/SeoIcons';
import { linkResolver } from '../../utils/linkResolver';

import { getBreadcrumb } from './getBreadcrumb';
import { getSchemaOrgAggregateRating } from './getSchemaOrgAggregateRating';
import { getSchemaOrgArticle } from './getSchemaOrgArticle';
import { getSchemaOrgFAQ } from './getSchemaOrgFAQ';
import { getSchemaOrgOrganization } from './getSchemaOrgOrganization';
import { getSchemaOrgReviews } from './getSchemaOrgReviews';
import { getSchemaOrgWebPage } from './getSchemaOrgWebPage';
import { getSchemaOrgWebSite } from './getSchemaOrgWebsite';
import { getSEOData } from './getSEOData';
import { getXDefaultLanguage } from './getXDefaultLanguage';

import { Color } from '../../constants/Colors';

export const Seo = ({
  defaultDocument,
  title,
  document,
  site,
  isArticle,
  parents,
  hasFAQ,
  FAQContent,
  reviews,
  hasNoRobots,
}) => {
  if (!defaultDocument || !document || !site) {
    return null;
  }

  const siteUrl = 'https://www.wooclap.com';
  const defaultAuthor = 'Wooclap';
  const { buildTime } = site || '';
  const {
    alternate_languages: alternateLanguages,
    lang,
    data: { author } = {},
  } = document || [];

  const seo = getSEOData({
    document,
    defaultDocument,
    title,
    siteUrl,
    isArticle,
    parents,
  });

  const hasHierarchy = !!isArticle || !!parents;
  const breadcrumb = getBreadcrumb({ hasHierarchy, seo, siteUrl, parents });
  const schemaOrgOrganization = getSchemaOrgOrganization({
    seo,
  });
  const schemaOrgWebPage = getSchemaOrgWebPage({
    seo,
    defaultAuthor,
    buildTime,
  });
  const schemaOrgWebSite = getSchemaOrgWebSite({ seo, defaultDocument });
  const schemaAggregateRating = reviews
    ? getSchemaOrgAggregateRating({ reviews })
    : {};
  const schemaReviewList = reviews ? getSchemaOrgReviews({ reviews }) : {};

  let schemaArticle = null;

  if (isArticle) {
    const authorData = author?.document?.data || {};
    const { first_name: firstName, last_name: lastName } = authorData || '';
    const authorName = `${firstName}${lastName ? ` ${lastName}` : ''}`;
    schemaArticle = getSchemaOrgArticle({ authorName, defaultAuthor, seo });
  }

  let schemaFAQ = null;

  if (hasFAQ) {
    schemaFAQ = getSchemaOrgFAQ(FAQContent);
  }

  // eslint-disable-next-line react/prop-types
  const hasReviews = reviews?.length >= 1 && reviews?.[0]?.review_body;

  const actualLanguage = {
    lang: seo.lang,
    url: seo.url.replace(seo.defaultUrl, '/'),
  };

  const xDefaultLanguage = getXDefaultLanguage(
    alternateLanguages,
    actualLanguage
  );

  return (
    <>
      <Helmet>
        <html lang={seo.lang} />
      </Helmet>
      <title>{seo.title}</title>
      {hasNoRobots && <meta content='none' name='robots' />}
      <meta charset='utf-8' />
      <meta content='Wooclap' name='author' />
      <meta content={seo.description} name='description' />
      <meta content={seo.image} name='image' />
      {/* Need to be sure the blog is working on IE11 */}
      {/* <meta content='IE=edge' httpEquiv='X-UA-Compatible' /> */}
      <meta
        content='width=device-width, initial-scale=1, user-scalable=yes'
        name='viewport'
      />

      {/* Alternate languages */}
      <link href={seo.url} rel='canonical' />
      <link
        href={`${siteUrl}${
          xDefaultLanguage.url
            ? xDefaultLanguage.url
            : linkResolver(xDefaultLanguage)
        }`}
        hrefLang='x-default'
        rel='alternate'
      />
      <link href={seo.url} hrefLang={lang.substr(0, 2)} rel='alternate' />
      {alternateLanguages.map((language, index) => (
        <link
          href={`${siteUrl}${linkResolver(language)}`}
          hrefLang={language.lang.substr(0, 2)}
          key={`${language}-${index}`}
          rel='alternate'
        />
      ))}

      {/* Icons */}
      <meta content={Color.BLUE_BIG_STONE} name='msapplication-TileColor' />
      <meta content={Color.BLUE_BIG_STONE} name='theme-color' />
      <meta content={favMs144} name='msapplication-TileImage' />

      {/* Facebook */}
      <meta content='Wooclap' property='og:site_name' />
      <meta content={seo.lang} property='og:locale' />
      <meta content={seo.url} property='og:url' />
      <meta content='website' property='og:type' />
      <meta content={seo.title} property='og:title' />
      <meta content={seo.ogDescription} property='og:description' />
      <meta content={seo.image} property='og:image' />
      <meta content={seo.imageAlt} property='og:image:alt' />
      <meta content='1200' data-react-helmet='true' property='og:image:width' />
      <meta content='627' data-react-helmet='true' property='og:image:height' />

      {/* Twitter */}
      <meta content='@wooclap' name='twitter:creator' />
      <meta content='summary_large_image' name='twitter:card' />
      <meta content={seo.title} name='twitter:title' />
      <meta content={seo.description} name='twitter:description' />
      <meta content={seo.image} name='twitter:image' />
      <meta content={seo.imageAlt} name='twitter:image:alt' />

      {/* Icons */}
      {seoIconsLinks.map((item, index) => (
        <link
          href={item.href}
          key={`${item}-${index}`}
          rel={item.rel}
          sizes={item.sizes}
          type={item.type}
        />
      ))}
      {/* Insert schema.org Organization */}
      <script type='application/ld+json'>
        {JSON.stringify(schemaOrgOrganization)}
      </script>

      {/* Insert schema.org WebSite */}
      <script type='application/ld+json'>
        {JSON.stringify(schemaOrgWebSite)}
      </script>

      {/* Insert schema.org data conditionally (webpage/article) + everytime (breadcrumbs) */}
      {!isArticle && (
        <script type='application/ld+json'>
          {JSON.stringify(schemaOrgWebPage)}
        </script>
      )}
      {isArticle && (
        <script type='application/ld+json'>
          {JSON.stringify(schemaArticle)}
        </script>
      )}

      {/* Insert schema.org FAQPage */}
      {hasFAQ && (
        <script type='application/ld+json'>{JSON.stringify(schemaFAQ)}</script>
      )}

      {/* Insert schema.org AggregateRating */}
      {hasReviews && (
        <script type='application/ld+json'>
          {JSON.stringify(schemaAggregateRating)}
        </script>
      )}

      {/* Insert schema.org Review */}
      {hasReviews &&
        schemaReviewList.length &&
        schemaReviewList.map((schemaReview, index) => (
          <script key={`${schemaReview}-${index}`} type='application/ld+json'>
            {schemaReview}
          </script>
        ))}

      <script type='application/ld+json'>{JSON.stringify(breadcrumb)}</script>

      {/* Do not track code */}
      <Script>
        {`
        window.wc = {
          // https://plainjs.com/javascript/utilities/set-cookie-get-cookie-and-delete-cookie-5/
          getCookie: function(name) {
            var v = document
              .cookie
              .match('(^|;) ?' + name + '=([^;]*)(;|$)');
            return v
              ? v[2]
              : null;
          },
          setCookie: function(name, value) {
            var d = new Date();
            // Expires in 10 years.
            d.setTime(d.getTime() + 1e3 * 60 * 60 * 24 * 365 * 10);
            document.cookie = name + '=' + value + ';domain=wooclap.com;path=/;expires=' + d.toGMTString();
          },
          Cookies: {
            DO_NOT_TRACK: 'wc__cookie-do-not-track'
          }
        };
        // If the URL contains the 'dnt=1' query string, it means that the user
        // doesn't want to be tracked by Google Analytics or Hotjar.
        // In that case, we set a cookie to disable those tools
        // Note: the regex can produce some false positives, but it's not a big
        // deal.
        if (/dnt=1/i.test(window.location.search)) {
          window
            .wc
            .setCookie(window.wc.Cookies.DO_NOT_TRACK, '1');
        }
      `}
      </Script>

      {/* VWO */}
      <link rel='preconnect' href='https://dev.visualwebsiteoptimizer.com' />
      <Script id='vwoCode'>
        {`window._vwo_code || (function() {
          var account_id=743018,
          version=2.0,
          settings_tolerance=2000,
          library_tolerance=2500,
          use_existing_jquery=false,
          hide_element='body',
          hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
          /* DO NOT EDIT BELOW THIS LINE */
          f=false,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?window.sessionStorage:window.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=this.getSettings(),n=d.createElement('script'),i=this;if(t){n.textContent=t;d.getElementsByTagName('head')[0].appendChild(n);if(!window.VWO||VWO.caE){stT.removeItem(cK);i.load(e)}}else{n.fetchPriority='high';n.src=e;n.type='text/javascript';n.onerror=function(){_vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(n)}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();window._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t=d.currentScript,n=d.createElement('style'),i=this.hide_element(),r=t&&!t.async&&i?i+this.hide_element_style():'',o=d.getElementsByTagName('head')[0];n.setAttribute('id','_vis_opt_path_hides');v&&n.setAttribute('nonce',v.nonce);n.setAttribute('type','text/css');if(n.styleSheet)n.styleSheet.cssText=r;else n.appendChild(d.createTextNode(r));o.appendChild(n);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version)}};window._vwo_code=code;code.init();})();`}
      </Script>

      {/* Google Tag Manager */}
      <Script strategy={ScriptStrategy.idle}>
        {`
        if (window.wc.getCookie(window.wc.Cookies.DO_NOT_TRACK) !== '1') {
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-TGGLGM6');
        }
      `}
      </Script>
    </>
  );
};

Seo.propTypes = {
  /**
   * Homepage document.
   */
  defaultDocument: PropTypes.shape(propTypesHomepage),
  /**
   * Current document: Category, Tag, Post, Homepage.
   */
  document: PropTypes.oneOfType([
    PropTypes.shape(propTypesHomepage),
    PropTypes.shape(propTypesPost),
    PropTypes.shape(propTypesCategory),
    PropTypes.shape(propTypesTag),
    PropTypes.shape(propTypesResource),
    PropTypes.shape(propTypesTestimonial),
    PropTypes.shape(propTypesWoobinar),
    PropTypes.shape(propTypesHomepageWeb),
    PropTypes.shape(propTypesPage),
    PropTypes.shape(propTypesContact),
    PropTypes.shape(propTypesEventCategory),
    PropTypes.shape(propTypesEventTag),
    PropTypes.shape(propTypesEvent),
    PropTypes.shape(propTypesNewsletter),
    PropTypes.shape(propTypesOrphans),
    PropTypes.shape(propTypesPricing),
  ]),
  /**
   * Content of FAQPage schema
   */
  FAQContent: PropTypes.shape(propTypesFAQContent),
  /**
   *
   */
  hasFAQ: PropTypes.bool,
  /**
   * Set the no robots meta tags if true.
   */
  hasNoRobots: PropTypes.bool,
  /**
   * SchemaOrg will be different if isArticle is true.
   */
  isArticle: PropTypes.bool,
  /**
   * Optional parent if the document type is from website.
   */
  parents: PropTypes.arrayOf(
    PropTypes.shape({
      parent_document: PropTypes.shape({
        document: PropTypes.shape(propTypesPage),
      }),
      parent_name: PropTypes.string,
    })
  ),
  /**
   * Content of the review schema
   */
  reviews: PropTypes.shape(propTypesReviews),
  /**
   * Site document.
   */
  site: PropTypes.shape(propTypesSite),
  /**
   * Meta title of the current page.
   */
  title: PropTypes.string.isRequired,
};
